"use client";

import { trackRetailerOnboardingAlertBannerView } from "@faire/web-api/events/retailerOnboarding/view/alertBanner";
import type { AlertBannerType } from "@faire/web-api/indigofair/data/AlertBannerType";
import { route as confirmIdentityRoute } from "@faire/web-api/routes/confirm-identity";
import some from "lodash/some";
import * as React from "react";
import { useRef, useEffect, useCallback } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";

import { IconBannerForwardRef } from "@faire/retailer/components/IconBanner";
import { useOwFNudgeEligibility } from "@faire/retailer/features/Nudges/OpenWithFaireNudge/useOwFNudgeEligibility";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useAlertBannerStore } from "@faire/retailer/packages/core/services/AlertBanner/AlertBannerStore";
import { shouldForceRefetchAlertBanner } from "@faire/retailer/packages/core/services/AlertBanner/utils";

const HIDE_ALERT_BANNER_PATHS = [confirmIdentityRoute];

interface IProps {
  clientSideOnly?: boolean;
  onBannerChange?: () => void;
}

export const AlertBanner: React.FC<IProps> = ({
  onBannerChange,
  clientSideOnly,
}) => {
  const prevRoute = useRef("");
  const { pathname } = useLocation();
  const isBannedPath = useIsBannedPath();
  const canRender = useCanRender({ clientSideOnly, onBannerChange });

  const {
    initialize,
    throttleFetchBanner,
    dismiss,
    dismissClientSideBanner,
    clientSideBanner,
    banner,
  } = useAlertBannerStore([
    "initialize",
    "throttleFetchBanner",
    "dismiss",
    "dismissClientSideBanner",
    "clientSideBanner",
    "banner",
  ]);
  const alertBannerWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initialize(onBannerChange);
  }, [initialize, onBannerChange]);

  const getBannerMessage = useCallback(() => {
    if (clientSideBanner) {
      const { title, message } = clientSideBanner;
      return title ? `${title} ${message}` : message;
    }

    return banner?.title
      ? `${banner?.title} ${banner?.message}`
      : banner?.message;
  }, [banner?.message, banner?.title, clientSideBanner]);

  useEffect(() => {
    trackRetailerOnboardingAlertBannerView(
      clientSideBanner?.type ?? banner?.type ?? "",
      getBannerMessage() ?? ""
    );
  }, [banner?.type, clientSideBanner?.type, getBannerMessage]);

  const handleLocationChanged = React.useCallback(
    (forceRefetch?: boolean) => {
      throttleFetchBanner({
        forceRefetch: forceRefetch ?? false,
      });

      if (isBannedPath) {
        onBannerChange?.();
      }
    },
    [throttleFetchBanner, isBannedPath, onBannerChange]
  );

  useEffect(() => {
    if (!isLoggedInRetailer()) {
      return;
    }

    const shouldForceRefetch = shouldForceRefetchAlertBanner([
      pathname,
      prevRoute.current,
    ]);

    // Update the previous route after the check
    prevRoute.current = pathname;

    handleLocationChanged(shouldForceRefetch);
  }, [handleLocationChanged, pathname]);

  const dismissBanner = (type?: keyof typeof AlertBannerType) => {
    if (type) {
      dismiss(type);
    }
  };

  if (clientSideBanner) {
    return (
      <IconBannerForwardRef
        clientInitiated
        value={clientSideBanner}
        onDismiss={dismissClientSideBanner}
        ref={alertBannerWrapperRef}
      />
    );
  }

  if (!canRender || !banner) {
    return null;
  }

  return (
    <IconBannerForwardRef
      value={banner}
      onDismiss={dismissBanner}
      ref={alertBannerWrapperRef}
    />
  );
};

const useIsBannedPath = () => {
  const history = useHistory();

  const bannedPath = some(
    HIDE_ALERT_BANNER_PATHS,
    (path: string) =>
      matchPath(history.location.pathname, { path, exact: true }) !== null
  );

  return bannedPath;
};

const useCanRender = (props: IProps) => {
  const isBannedPath = useIsBannedPath();

  const { clientSideBanner, banner } = useAlertBannerStore([
    "clientSideBanner",
    "banner",
  ]);
  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();

  const { shouldShowOwFNudge } = useOwFNudgeEligibility();
  const hideBannerForOwFNudge =
    (banner?.type === "CLOSE_TO_NET_LIMITS" ||
      banner?.type === "ABOVE_PAYMENT_TERM_LIMIT" ||
      banner?.type === "OPEN_WITH_FAIRE_APPLICATION_STARTED") &&
    shouldShowOwFNudge;

  if (clientSideBanner) {
    return true;
  }

  const shouldHide =
    props.clientSideOnly ||
    isFaireDirectActiveInLoggedOut ||
    !banner ||
    isBannedPath ||
    hideBannerForOwFNudge;

  return !shouldHide;
};
